// Item history: every change to the item and everything on it (responses, comments, files, follow-ups, emails) —
// who, when, before → after. Collapsed by default; the Super Admin can undo a change from here.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { undo } from '../../lib/api';
import { useApp } from '../../lib/app';
import { fmtDateTime } from '../../lib/format';
import { Button, errText, useToast } from '../kit';

type Row = {
  id: number;
  at: string;
  user_id: string | null;
  user_name: string | null;
  device: string | null;
  batch_id: string | null;
  table_name: string;
  row_id: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  summary: string | null;
  undone_by: number | null;
  undo_of: number | null;
  total: number;
};

const CATS: { key: string; label: string; tables: string[] }[] = [
  { key: 'all', label: 'All', tables: [] },
  { key: 'item', label: 'Status & fields', tables: ['items'] },
  { key: 'resp', label: 'Responses & follow-ups', tables: ['item_responses', 'follow_ups'] },
  { key: 'files', label: 'Files', tables: ['files'] },
  { key: 'comments', label: 'Comments', tables: ['comments'] },
  { key: 'emails', label: 'Emails', tables: ['item_emails'] },
];
const THING: Record<string, string> = { items: '', item_responses: 'Response', follow_ups: 'Follow-up', files: 'File', comments: 'Comment', item_emails: 'Email' };
const LABEL: Record<string, string> = {
  title: 'Topic', status: 'Status', stage: 'Stage', subtype: 'Type', ref_no: 'Ref', building_ids: 'Buildings', directed_to: 'Directed to', org_id: 'Contractor',
  submitted_on: 'Submitted', respond_by: 'Respond by', amount: 'Amount', notes: 'Notes', remark: 'Remark', response: 'Response', onus_org_id: 'Onus',
  eot_days_claimed: 'EOT days claimed', eot_days_granted: 'EOT days granted', review_ok: 'Checked', body: 'Text', visibility: 'Who can see', done_at: 'Done',
  due_on: 'Due', assignee_id: 'Assigned to', from_org_id: 'From', notes_file: 'Note', original_name: 'Name', subject: 'Subject', is_current: 'Current version',
};
const SKIP = new Set(['updated_at', 'updated_by', 'created_at', 'created_by', 'review', 'c3_edited_at', 'onus_since', 'edited_at', 'id', 'site_id', 'item_id', 'linked_id', 'linked_table']);
const via = (d: string | null) =>
  !d ? '—' : d === 'smartsheet-import' ? 'Import' : d === 'system' ? 'System' : /mobile|iphone|android/i.test(d) ? 'Phone' : d === 'test' ? 'Test' : 'Web';

function show(v: unknown, names: (k: string, v: unknown) => string | null, k: string): string {
  const n = names(k, v);
  if (n !== null) return n;
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map((x) => names(k, x) ?? String(x)).join(', ') : '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 90 ? s.slice(0, 88) + '…' : s;
}

export function History({ itemId, names, version }: { itemId: string; names: (key: string, value: unknown) => string | null; /** changes when anything on the item changes → reload */ version: string }) {
  const { access } = useApp();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [head, setHead] = useState<Row | null>(null);
  const [cat, setCat] = useState('all');
  const [limit, setLimit] = useState(50);
  const [err, setErr] = useState<string | null>(null);
  const admin = Boolean(access?.super_admin);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('c3_item_history', { p_item: itemId, p_limit: open ? limit : 1, p_before: null });
    if (error) return setErr(error.message);
    setErr(null);
    const list = (data ?? []) as Row[];
    setHead(list[0] ?? null);
    if (open) setRows(list);
  }, [itemId, open, limit]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 400);
    return () => clearTimeout(t);
  }, [load, version]);

  const tables = CATS.find((c) => c.key === cat)?.tables ?? [];
  const shown = rows.filter((r) => !tables.length || tables.includes(r.table_name));
  const total = head?.total ?? 0;

  async function doUndo(r: Row) {
    if (!window.confirm('Undo this change? The undo is logged too.')) return;
    try {
      const res = await undo({ audit_id: r.id });
      if (!res.ok) toast({ tone: 'error', text: 'Changed again since — open Admin → Audit log to undo with care.' });
      else toast({ tone: 'ok', text: 'Undone' });
      void load();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <section className="hist">
      <button type="button" className={`histh ${open ? 'open' : ''}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M9 6l6 6-6 6" />
        </svg>
        <span className="k">Item history</span>
        <span className="muted small">
          {total} change{total === 1 ? '' : 's'}
          {head ? ` · last: ${fmtDateTime(head.at)} · ${head.user_name ?? 'System'}` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <span className="hist-hint">{open ? 'Hide' : 'Show all changes'}</span>
      </button>
      {open ? (
        <div>
          <div className="hist-filters" role="radiogroup" aria-label="Filter history">
            {CATS.map((c) => (
              <button key={c.key} type="button" role="radio" aria-checked={cat === c.key} className={`chip ${cat === c.key ? 'on' : ''}`} onClick={() => setCat(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          {err ? <div className="error-box">{err}</div> : null}
          <div className="table-wrap">
            <table className="t htab">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Change</th>
                  <th>Via</th>
                  {admin ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const keys =
                    r.action === 'update' && r.before && r.after
                      ? Object.keys(r.after).filter((k) => !SKIP.has(k) && JSON.stringify(r.before![k]) !== JSON.stringify(r.after![k]))
                      : [];
                  const thing = THING[r.table_name] ?? r.table_name;
                  const what = r.summary || `${thing ? thing + ' ' : ''}${r.action}`;
                  return (
                    <tr key={r.id} className={r.undone_by ? 'undone' : ''}>
                      <td className="small nowrap">{fmtDateTime(r.at)}</td>
                      <td className="small">{r.user_name ?? 'System'}</td>
                      <td className="small">
                        <strong>{what}</strong>
                        {r.undone_by ? <span className="muted"> · undone</span> : null}
                      </td>
                      <td className="small">
                        {keys.length
                          ? keys.slice(0, 4).map((k) => (
                              <div key={k}>
                                <span className="muted">{LABEL[k] ?? k}: </span>
                                <span className="was">{show(r.before![k], names, k)}</span> → {show(r.after![k], names, k)}
                              </div>
                            ))
                          : r.action === 'insert'
                            ? 'Added'
                            : r.action === 'delete'
                              ? 'Deleted'
                              : r.action === 'restore'
                                ? 'Restored'
                                : r.action === 'undo'
                                  ? 'Undo'
                                  : ''}
                      </td>
                      <td>
                        <span className="via">{via(r.device)}</span>
                      </td>
                      {admin ? (
                        <td className="right">
                          {!r.undone_by && r.action !== 'undo' ? (
                            <button type="button" className="linkbtn small" onClick={() => void doUndo(r)}>
                              Undo
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="count-line">
            Showing {shown.length} of {total}
            {rows.length < total ? (
              <>
                {' · '}
                <Button kind="ghost" onClick={() => setLimit(limit + 100)}>
                  Load older
                </Button>
              </>
            ) : null}
            <span style={{ float: 'right' }}>Every change is logged with who, when, device and before/after.</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
